import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Trash2, Settings, Target, Calendar, Clock, Pencil, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RankingEditor } from "@/components/RankingEditor";

const emptyTour = { name: "", date: "", time: "20:00", is_doubles: false, payment_link: "", entry_fee: 6, counts_for_ranking: true, dartconnect_link: "" };

const emptyFinish = { player_id: "", value: "", tournament_id: "" };

export default function AdminDashboard() {
  const [players, setPlayers] = useState([]);
  const [tours, setTours] = useState([]);
  const [newName, setNewName] = useState("");
  const [newTour, setNewTour] = useState(emptyTour);
  const [editTour, setEditTour] = useState(null); // tournament being edited
  const [editForm, setEditForm] = useState(emptyTour);
  const [finishes, setFinishes] = useState([]);
  const [newFinish, setNewFinish] = useState(emptyFinish);
  const [editPlayer, setEditPlayer] = useState(null); // { id, name }

  const load = () => {
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/tournaments").then((r) => setTours(r.data));
    api.get("/high-finishes").then((r) => setFinishes(r.data));
  };
  useEffect(load, []);

  const addFinish = async (e) => {
    e.preventDefault();
    if (!newFinish.player_id) return toast.error("Kies een speler");
    const val = Number(newFinish.value);
    if (!val || val < 99) return toast.error("Finish moet minimaal 99 zijn");
    if (val > 170) return toast.error("Finish kan maximaal 170 zijn");
    try {
      await api.post("/high-finishes", {
        player_id: newFinish.player_id,
        value: val,
        tournament_id: newFinish.tournament_id || null,
      });
      toast.success("Finish toegevoegd");
      setNewFinish(emptyFinish);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delFinish = async (id) => {
    await api.delete(`/high-finishes/${id}`);
    toast.success("Finish verwijderd");
    load();
  };

  const addPlayer = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/players", { name: newName.trim() });
      setNewName("");
      toast.success("Speler toegevoegd");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delPlayer = async (id) => {
    await api.delete(`/players/${id}`);
    toast.success("Speler verwijderd");
    load();
  };

  const savePlayerName = async () => {
    if (!editPlayer.name.trim()) return toast.error("Naam mag niet leeg zijn");
    try {
      await api.patch(`/players/${editPlayer.id}`, { name: editPlayer.name.trim() });
      toast.success("Naam bijgewerkt");
      setEditPlayer(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (t, status) => {
    await api.patch(`/tournaments/${t.id}`, { status });
    toast.success(`${t.name} → ${status}`);
    load();
  };

  const toDdMmYyyy = (iso) => {
    if (!iso) return null;
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };

  const addTournament = async (e) => {
    e.preventDefault();
    if (!newTour.name.trim()) return toast.error("Vul een naam in");
    try {
      await api.post("/tournaments", {
        name: newTour.name.trim(),
        date: toDdMmYyyy(newTour.date),
        time: newTour.time || null,
        is_doubles: newTour.is_doubles,
        payment_link: newTour.payment_link.trim() || null,
        entry_fee: Number(newTour.entry_fee) || 0,
        counts_for_ranking: newTour.counts_for_ranking,
        dartconnect_link: newTour.dartconnect_link.trim() || null,
      });
      toast.success("Toernooi toegevoegd");
      setNewTour(emptyTour);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delTournament = async (t) => {
    if (!window.confirm(`Toernooi "${t.name}" verwijderen? Alle resultaten en inschrijvingen gaan verloren.`)) return;
    await api.delete(`/tournaments/${t.id}`);
    toast.success("Toernooi verwijderd");
    load();
  };

  const toIso = (dd) => {
    if (!dd) return "";
    const [d, m, y] = dd.split("-");
    return d && m && y ? `${y}-${m}-${d}` : "";
  };

  const openEdit = (t) => {
    setEditTour(t);
    setEditForm({ name: t.name, date: toIso(t.date), time: t.time || "", is_doubles: !!t.is_doubles, payment_link: t.payment_link || "", entry_fee: t.entry_fee ?? 6, counts_for_ranking: t.counts_for_ranking !== false, dartconnect_link: t.dartconnect_link || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) return toast.error("Vul een naam in");
    try {
      await api.patch(`/tournaments/${editTour.id}`, {
        name: editForm.name.trim(),
        date: toDdMmYyyy(editForm.date),
        time: editForm.time || null,
        is_doubles: editForm.is_doubles,
        payment_link: editForm.payment_link.trim() || null,
        entry_fee: Number(editForm.entry_fee) || 0,
        counts_for_ranking: editForm.counts_for_ranking,
        dartconnect_link: editForm.dartconnect_link.trim(),
      });
      toast.success("Toernooi bijgewerkt");
      setEditTour(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-16">
      <h1 className="font-heading text-4xl lg:text-5xl tracking-tighter font-black mb-1">Beheer Dashboard</h1>
      <p className="text-[#525252] mb-10">Beheer spelers, toernooistatus en resultaten.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Players */}
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-xl font-bold">Spelers ({players.length})</h2>
          </div>
          <form onSubmit={addPlayer} className="flex gap-2 mb-4">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="player-name-input"
              placeholder="Nieuwe spelernaam"
              className="flex-1 border-b-2 border-[#E5E5E5] bg-transparent py-2 px-1 focus:outline-none focus:border-[#D4AF37]"
            />
            <button data-testid="add-player-btn" className="bg-[#D4AF37] text-[#0A0A0A] px-3 font-bold hover:bg-[#A8882D] transition-colors">
              <Plus className="w-5 h-5" />
            </button>
          </form>
          <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5] max-h-[420px] overflow-y-auto">
            {players.length === 0 && <p className="p-4 text-sm text-[#525252]">Nog geen spelers.</p>}
            {players.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 px-4 py-2.5" data-testid={`player-row-${p.id}`}>
                {editPlayer?.id === p.id ? (
                  <>
                    <input
                      value={editPlayer.name}
                      onChange={(e) => setEditPlayer({ ...editPlayer, name: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") savePlayerName(); if (e.key === "Escape") setEditPlayer(null); }}
                      data-testid={`edit-player-input-${p.id}`}
                      autoFocus
                      className="flex-1 border-b-2 border-[#D4AF37] bg-transparent py-1 px-1 text-sm focus:outline-none"
                    />
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={savePlayerName} data-testid={`save-player-${p.id}`} className="text-[#16A34A] hover:text-[#15803D]"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditPlayer(null)} data-testid={`cancel-player-${p.id}`} className="text-[#A0A0A0] hover:text-[#0A0A0A]"><X className="w-4 h-4" /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-medium">{p.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setEditPlayer({ id: p.id, name: p.name })} data-testid={`edit-player-${p.id}`} className="text-[#525252] hover:text-[#D4AF37]">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => delPlayer(p.id)} data-testid={`delete-player-${p.id}`} className="text-[#A0A0A0] hover:text-[#DC2626]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tournaments */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-xl font-bold">Toernooien</h2>
          </div>

          <form onSubmit={addTournament} className="bg-white border border-[#E5E5E5] p-4 mb-5" data-testid="new-tournament-form">
            <p className="text-xs uppercase tracking-wider font-semibold text-[#525252] mb-3">Nieuw toernooi toevoegen</p>
            <div className="space-y-3">
              <input
                value={newTour.name}
                onChange={(e) => setNewTour({ ...newTour, name: e.target.value })}
                data-testid="new-tour-name"
                placeholder="Naam toernooi"
                className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#525252] flex items-center gap-1"><Calendar className="w-3 h-3" /> Datum</span>
                <input type="date" value={newTour.date} onChange={(e) => setNewTour({ ...newTour, date: e.target.value })} data-testid="new-tour-date" className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#525252] flex items-center gap-1"><Clock className="w-3 h-3" /> Tijd</span>
                <input type="time" value={newTour.time} onChange={(e) => setNewTour({ ...newTour, time: e.target.value })} data-testid="new-tour-time" className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[#525252]">Inschrijfgeld (€)</span>
                <input type="number" step="0.01" min="0" value={newTour.entry_fee} onChange={(e) => setNewTour({ ...newTour, entry_fee: e.target.value })} data-testid="new-tour-fee" className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </label>
            </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={newTour.is_doubles} onChange={(e) => setNewTour({ ...newTour, is_doubles: e.target.checked })} data-testid="new-tour-doubles" className="w-4 h-4 accent-[#D4AF37]" />
                Koppeltoernooi (2 spelers per inschrijving)
              </label>
              <button data-testid="add-tournament-btn" className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] px-4 py-2 text-sm font-semibold hover:bg-[#1A1A1A] transition-colors">
                <Plus className="w-4 h-4" /> Toevoegen
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer mt-3">
              <input type="checkbox" checked={newTour.counts_for_ranking} onChange={(e) => setNewTour({ ...newTour, counts_for_ranking: e.target.checked })} data-testid="new-tour-counts" className="w-4 h-4 accent-[#D4AF37]" />
              Telt mee voor de totale stand (zet uit voor vakantietoernooien)
            </label>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">Betaallink (optioneel — Tikkie / PayPal / Mollie)</label>
              <input
                value={newTour.payment_link}
                onChange={(e) => setNewTour({ ...newTour, payment_link: e.target.value })}
                data-testid="new-tour-payment-link"
                placeholder="https://..."
                className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">DartConnect-link (optioneel)</label>
              <input
                value={newTour.dartconnect_link}
                onChange={(e) => setNewTour({ ...newTour, dartconnect_link: e.target.value })}
                data-testid="new-tour-dartconnect-link"
                placeholder="https://tv.dartconnect.com/..."
                className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </form>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tours.map((t) => (
              <div key={t.id} className="bg-white border border-[#E5E5E5] p-4 flex items-center justify-between" data-testid={`admin-tour-${t.number}`}>
                <div className="flex items-center gap-3">
                  <span className="font-heading text-2xl font-black text-[#E5E5E5]">{t.name === "Finale" ? "F" : String(t.number).padStart(2, "0")}</span>
                  <div>
                    <p className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                      {t.name}
                      {t.is_doubles && <span className="text-[9px] uppercase tracking-wide bg-[#D4AF37] text-[#0A0A0A] px-1.5 py-0.5 font-bold">Koppel</span>}
                      {t.counts_for_ranking === false && <span className="text-[9px] uppercase tracking-wide bg-[#525252] text-white px-1.5 py-0.5 font-bold" data-testid={`no-ranking-badge-${t.number}`}>Telt niet mee</span>}
                    </p>
                    <p className="text-xs text-[#525252]">{t.date || "—"}{t.time ? ` · ${t.time}` : ""}</p>
                    <select
                      value={t.status}
                      onChange={(e) => setStatus(t, e.target.value)}
                      data-testid={`status-select-${t.number}`}
                      className="text-xs text-[#525252] bg-transparent focus:outline-none cursor-pointer"
                    >
                      <option value="upcoming">aankomend</option>
                      <option value="live">live</option>
                      <option value="completed">afgerond</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(t)} data-testid={`edit-tour-${t.number}`} className="text-[#525252] hover:text-[#D4AF37] p-2" title="Bewerken"><Pencil className="w-4 h-4" /></button>
                  <Link
                    to={`/admin/tournament/${t.id}`}
                    data-testid={`manage-${t.number}`}
                    className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] px-3 py-2 text-xs font-semibold hover:bg-[#1A1A1A] transition-colors"
                  >
                    <Settings className="w-3.5 h-3.5" /> Beheren
                  </Link>
                  <button onClick={() => delTournament(t)} data-testid={`delete-tour-${t.number}`} className="text-[#A0A0A0] hover:text-[#DC2626] p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hoogste finishes */}
      <div className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="font-heading text-xl font-bold">Hoogste finishes ({finishes.length})</h2>
        </div>

        <form onSubmit={addFinish} className="bg-white border border-[#E5E5E5] p-4 mb-5 grid grid-cols-1 sm:grid-cols-[1fr_120px_1fr_auto] gap-3 items-end" data-testid="new-finish-form">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#525252]">Speler</span>
            <select
              value={newFinish.player_id}
              onChange={(e) => setNewFinish({ ...newFinish, player_id: e.target.value })}
              data-testid="new-finish-player"
              className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="">Kies speler…</option>
              {players.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#525252]">Finish (99+)</span>
            <input
              type="number"
              value={newFinish.value}
              onChange={(e) => setNewFinish({ ...newFinish, value: e.target.value })}
              data-testid="new-finish-value"
              placeholder="bv. 170"
              className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-[#525252]">Toernooi (optioneel)</span>
            <select
              value={newFinish.tournament_id}
              onChange={(e) => setNewFinish({ ...newFinish, tournament_id: e.target.value })}
              data-testid="new-finish-tournament"
              className="border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="">Geen toernooi</option>
              {tours.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <button data-testid="add-finish-btn" className="inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-[#D4AF37] px-4 py-2.5 text-sm font-semibold hover:bg-[#1A1A1A] transition-colors">
            <Plus className="w-4 h-4" /> Toevoegen
          </button>
        </form>

        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          {finishes.length === 0 ? (
            <p className="p-6 text-sm text-[#525252]">Nog geen finishes ingevoerd.</p>
          ) : (
            <table className="w-full" data-testid="admin-finishes-table">
              <thead>
                <tr className="bg-[#FDFBF7] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
                  <th className="text-left py-3 px-4 w-12">#</th>
                  <th className="text-left py-3 px-4">Finish</th>
                  <th className="text-left py-3 px-4">Speler</th>
                  <th className="text-left py-3 px-4">Toernooi</th>
                  <th className="py-3 px-4 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {finishes.map((f, idx) => (
                  <tr key={f.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FDFBF7]" data-testid={`admin-finish-row-${f.id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs text-[#A0A0A0]">{idx + 1}</td>
                    <td className="py-2.5 px-4 font-mono text-lg font-bold text-[#A8882D]">{f.value}</td>
                    <td className="py-2.5 px-4 text-sm font-semibold">{f.player_name}</td>
                    <td className="py-2.5 px-4 text-sm text-[#525252]">{f.tournament_name || "—"}</td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => delFinish(f.id)} data-testid={`delete-finish-${f.id}`} className="text-[#A0A0A0] hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <RankingEditor />

      {/* Edit tournament dialog */}
      <Dialog open={!!editTour} onOpenChange={(v) => !v && setEditTour(null)}>
        <DialogContent className="rounded-none border-2 border-[#0A0A0A] sm:max-w-md" data-testid="edit-tournament-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">Toernooi bewerken</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="edit-tournament-form">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">Naam</label>
              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-tour-name" className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#525252] flex items-center gap-1"><Calendar className="w-3 h-3" /> Datum</label>
                <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-tour-date" className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#525252] flex items-center gap-1"><Clock className="w-3 h-3" /> Tijd</label>
                <input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} data-testid="edit-tour-time" className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#525252]">Inschrijfgeld (€)</label>
                <input type="number" step="0.01" min="0" value={editForm.entry_fee} onChange={(e) => setEditForm({ ...editForm, entry_fee: e.target.value })} data-testid="edit-tour-fee" className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={editForm.is_doubles} onChange={(e) => setEditForm({ ...editForm, is_doubles: e.target.checked })} data-testid="edit-tour-doubles" className="w-4 h-4 accent-[#D4AF37]" />
              Koppeltoernooi (2 spelers per inschrijving)
            </label>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={editForm.counts_for_ranking} onChange={(e) => setEditForm({ ...editForm, counts_for_ranking: e.target.checked })} data-testid="edit-tour-counts" className="w-4 h-4 accent-[#D4AF37]" />
              Telt mee voor de totale stand
            </label>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">Betaallink (optioneel)</label>
              <input value={editForm.payment_link} onChange={(e) => setEditForm({ ...editForm, payment_link: e.target.value })} data-testid="edit-tour-payment-link" placeholder="https://..." className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#525252]">DartConnect-link (optioneel)</label>
              <input value={editForm.dartconnect_link} onChange={(e) => setEditForm({ ...editForm, dartconnect_link: e.target.value })} data-testid="edit-tour-dartconnect-link" placeholder="https://tv.dartconnect.com/..." className="w-full border border-[#E5E5E5] bg-white py-2 px-2 text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <DialogFooter>
              <button type="submit" data-testid="save-tour-edit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#D4AF37] py-3 font-semibold hover:bg-[#1A1A1A] transition-colors">
                <Pencil className="w-4 h-4" /> Opslaan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
